import { CONSTANTS } from './Configuration';
import { v4 as uuidv4 } from 'uuid';
import cloneDeep from 'lodash/cloneDeep';
const { templateMessageKeys, templateRichTextMessageKeys, languagesPrefix } = CONSTANTS;
const { langPrefix, regionPrefix } = languagesPrefix;

const safeDecodeURI = value => decodeURI(value || '');

export const swapGlobalMessageIdsWithMessageValues = (result, setting, useSettingNameForKey = false) => {
    if (!result || !setting) {
        return;
    }
    const language = result.defaultLanguage;
    const messages = result.settingMessages.languages[language]?.messages;
    const key = useSettingNameForKey ? setting.settingName : setting.settingValue;
    if (setting.localized === true && messages) {
        setting.localizedValue = setting.isHtml ?
            safeDecodeURI(messages[key]) : messages[key];
    }
};

export const swapGlobalMessageValuesWithMessageIds = (globalSettings) => {
    if (!globalSettings) {
        return;
    }
    const language = globalSettings.defaultLanguage;
    const messages = globalSettings.settingMessages.languages[language]?.messages;
    globalSettings.generalSettings.forEach(x => {
        if (x.localized === true && messages) {
            messages[x.settingName] = x.isHtml ?
                encodeURI(x.localizedValue) : x.localizedValue;
        }
    });
    for (const [name, setting] of Object.entries(globalSettings.applicationRetrievalSettings)) {
        if (setting.localized === true && messages) {
            messages[name] = setting.isHtml ?
                encodeURI(setting.localizedValue) : setting.localizedValue;
        }
    }
    return globalSettings;
};

export const swapTemplateMessageIdsWithMessages = (template) => {
    const language = template.defaultLanguage;
    const { messages } = template.templateMessages.languages[language];
    // Template
    templateMessageKeys.template.forEach(x => {
        const messageId = template[`${x}Id`];
        if (messageId) {
            template[x] = templateRichTextMessageKeys.template.includes(x)? safeDecodeURI(messages[messageId]): messages[messageId] || '';
        }
    });
    // Settings
    if (template.settings) {
        templateMessageKeys.settings.forEach(x => {
            const messageId = template.settings[`${x}Id`];
            if (messageId) {
                template.settings[x] = templateRichTextMessageKeys.settings.includes(x)? safeDecodeURI(messages[messageId]): messages[messageId] || '';
            }
        });
    }

    // Supplemental Items
    if (template.supplementalItems) {
        templateMessageKeys.supplementalItems.forEach(x => {
            const messageId = template.supplementalItems[`${x}Id`];
            if (messageId) {
                template.supplementalItems[x] = templateRichTextMessageKeys.supplementalItems.includes(x)?  safeDecodeURI(messages[messageId]): messages[messageId] || '';
            }
        });
    }
    if (template.supplementalItems?.items) {
        template.supplementalItems.items.forEach(i => {
            templateMessageKeys.items.forEach(x => {
                const messageId = i[`${x}Id`];
                if (messageId) {
                    i[x] = templateRichTextMessageKeys.items.includes(x)? safeDecodeURI(messages[messageId]): messages[messageId] || '';
                }
            });
        });
    }
    // Pages
    if (template.pages) {
        template.pages.forEach(p => {
            templateMessageKeys.pages.forEach(x => {
                const messageId = p[`${x}Id`];
                if (messageId) {
                    p[x] = templateRichTextMessageKeys.pages.includes(x)?  safeDecodeURI(messages[messageId]): messages[messageId] || '';
                }
            });
            // Sections
            p.sections?.forEach(s => {
                templateMessageKeys.sections.forEach(x => {
                    const messageId = s[`${x}Id`];
                    if (messageId) {
                        s[x] = templateRichTextMessageKeys.sections.includes(x)?  safeDecodeURI(messages[messageId]): messages[messageId] || '';
                    }
                });
                // Questions
                s.groups?.forEach(g => {
                    g.questions?.forEach(q => {
                        templateMessageKeys.questions.forEach(x => {
                            const messageId = q[`${x}Id`];
                            if (messageId) {
                                q[x] = templateRichTextMessageKeys.questions.includes(x)? safeDecodeURI(messages[messageId]): messages[messageId] || '';
                            }
                        });
                        // Possible Values
                        q = possibleValueMessageHelper(q, messages);
                        // Filtered Search
                        q = filteredSearchMessageHelper(q, messages);
                    });
                });
            });
        });
    }
    return template;
};

export const possibleValueMessageHelper = (q, messages) => {
    if (q.possibleValues && q.possibleValues.values) {
        q.possibleValues.values.forEach(v => {
            if (v.labelId) {
                v.label = messages[v.labelId];
            }
        });
    }
    return q;
};

export const filteredSearchMessageHelper = (q, messages) => {
    if (q?.filteredSearch) {
        if (q.filteredSearch?.attributes) {
            q.filteredSearch.attributes?.forEach((attribute) => {
                if (attribute?.displayLabelId) {
                    attribute.displayLabel = messages[attribute.displayLabelId];
                }
            });
        }
        if (q.filteredSearch?.displayAttributes) {
            q.filteredSearch.displayAttributes?.forEach((attribute) => {
                if (attribute?.displayLabelId) {
                    attribute.displayLabel = messages[attribute.displayLabelId];
                }
            });
        }
        if (q.filteredSearch?.recordNotFoundAttribute) {
            const recordNotFoundAttribute = q.filteredSearch?.recordNotFoundAttribute;
            if (recordNotFoundAttribute?.displayLabelId) {
                recordNotFoundAttribute.displayLabel = messages[recordNotFoundAttribute.displayLabelId];
            }
        }
    }
    return q;
};

export const swapTemplateMessagesWithMessageIds = (template) => {
    const language = template.defaultLanguage;
    const messages = {};
    // Template
    templateMessageKeys.template.forEach(x => {
        assignMessageId(template, messages, x, templateRichTextMessageKeys.template.includes(x));
    });
    // Settings
    if (template.settings) {
        templateMessageKeys.settings.forEach(x => {
            assignMessageId(template.settings, messages, x, templateRichTextMessageKeys.settings.includes(x));
        });
    }
    // Supplemental Items
    if (template.supplementalItems) {
        templateMessageKeys.supplementalItems.forEach(i => {
            assignMessageId(template.supplementalItems, messages, i, templateRichTextMessageKeys.supplementalItems.includes(i));
        });
    }
    if (template.supplementalItems?.items) {
        template.supplementalItems.items.forEach(i => {
            templateMessageKeys.items.forEach(x => {
                assignMessageId(i, messages, x, templateRichTextMessageKeys.items.includes(x));
            });
        });
    }
    // Pages
    if (template.pages) {
        template.pages.forEach(p => {
            templateMessageKeys.pages.forEach(x => {
                assignMessageId(p, messages, x, templateRichTextMessageKeys.pages.includes(x));
            });
            // Sections
            p.sections && p.sections.forEach(s => {
                templateMessageKeys.sections.forEach(x => {
                    assignMessageId(s, messages, x, templateRichTextMessageKeys.sections.includes(x));
                });
                // Questions
                s.groups && s.groups.forEach(g => {
                    g.questions.forEach(q => {
                        templateMessageKeys.questions.forEach(x => {
                            assignMessageId(q, messages, x, templateRichTextMessageKeys.questions.includes(x));
                        });
                        // Possible Values
                        if (q.possibleValues && q.possibleValues.values) {
                            q.possibleValues.values.forEach(v => {
                                assignMessageId(v, messages, 'label');
                            });
                        }
                        // Filtered Search
                        if (q?.filteredSearch) {
                            if (q.filteredSearch?.attributes) {
                                q.filteredSearch.attributes?.forEach((attribute) => {
                                    if (attribute?.displayLabel) {
                                        assignMessageId(attribute, messages, 'displayLabel');
                                    }
                                });
                            }
                            if (q.filteredSearch?.displayAttributes) {
                                q.filteredSearch.displayAttributes?.forEach((attribute) => {
                                    if (attribute?.displayLabel) {
                                        assignMessageId(attribute, messages, 'displayLabel');
                                    }
                                });
                            }
                            if (q.filteredSearch?.recordNotFoundAttribute?.displayLabel) {
                                const recordNotFoundAttribute = q.filteredSearch.recordNotFoundAttribute;
                                assignMessageId(recordNotFoundAttribute, messages, 'displayLabel');
                            }
                        }
                    });
                });
            });
        });
    }
    template.templateMessages.languages[language].messages = messages;
};

const assignMessageId = (obj, messages, property, isRichText) => {
    const textValue = obj[property];
    let messageId = obj[`${property}Id`];
    delete obj[property];
    if (!textValue && !messageId) {
        return;
    }
    if (!textValue && messageId) {
        delete obj[`${property}Id`];
        return;
    }
    if (textValue && !messageId) {
        messageId = obj[`${property}Id`] = uuidv4();
    }
    messages[messageId] = isRichText ? encodeURI(textValue) : textValue;
};

export const syncMessagesWithDefaultLanguage = (template) => {
    if (template.defaultLanguage && template.templateMessages && template.templateMessages.languages &&
        template.templateMessages.languages[template.defaultLanguage]) {
        const defaultMessages = template.templateMessages.languages[template.defaultLanguage].messages;
        Object.keys(template.templateMessages.languages).forEach(l => {
            if (l !== template.defaultLanguage) {
                const messages = template.templateMessages.languages[l].messages;
                let newMessages = cloneDeep(defaultMessages);
                Object.keys(messages).forEach(m => {
                    if (newMessages[m]) {
                        newMessages[m] = messages[m] || newMessages[m];
                    }
                });
                template.templateMessages.languages[l].messages = newMessages;
            }
        });
    }
};

export const getDisplayForLocale = (locale, formatMessage) => {
    const localeArray = locale.split('-');
    let localeDisplayLabel = '';
    if (localeArray.length > 1) {
        localeDisplayLabel = `${formatMessage({ id: `${langPrefix}${localeArray[0]}` })} (${formatMessage({ id: `${regionPrefix}${localeArray[1]}` })})`;
    }
    else {
        localeDisplayLabel = formatMessage({ id: `${langPrefix}${locale}` });
    }
    return localeDisplayLabel;
};
